'use client';

import { HTMLProps, ReactNode, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import Image from 'next/image';
import Link, { LinkProps } from 'next/link';

import ArrowRight from '@/assets/images/arrow-right-btn.svg';
import logo from '@/assets/images/logo-new-footer.svg';
import MoveToTopImage from '@/assets/images/top-arrow.svg';
import ButtonLink from '@/components/common/ButtonLink';
import Form, { CustomFormData } from '@/components/FormFields';
import Button from '@/components/FormFields/Button';
import TextInput from '@/components/FormFields/TextInput';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { useAppContext } from '@/layouts/AppProvider';
import Container from '@/layouts/Container';
import { useNewsletterSignUpMutation } from '@/store/api/newsletter';
import { ResponseError } from '@/types/Error';
import { SendOutlined } from '@mui/icons-material';

import '@/styles/globals.v2.css';

type RawFootLinkProps = {
  className?: string;
  href: string;
} & (
  | {
      label?: string;
      children: ReactNode;
    }
  | {
      label: string;
      children?: ReactNode;
    }
);

type FootLinkProps = RawFootLinkProps &
  (
    | ({
        as?: 'Link';
      } & Omit<LinkProps, 'href' | 'ref'>)
    | ({
        as?: 'a';
        // href: string;
      } & Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'ref'>)
  );

type ChildrenAble = {
  children: ReactNode | ReactNode[];
};

type FootBannerProps = ChildrenAble;
type LinksColumnProps = ChildrenAble;
type SocialConnectionsColumnProps = ChildrenAble;

type SocialLinkProps = {
  href: string;
  children: ReactNode;
};

function BrandingColumn() {
  const { isBackOffice } = useAppContext();
  const { generateURL, webalias } = useWebAlias();
  const { customer } = useAuthContext();
  const logoPath = isBackOffice ? `https://shop.pawtree.com/${customer?.webAlias}` : generateURL('/', webalias);

  return (
    <div className="flex flex-col items-center sm:items-start sm:pr-0 md:pr-[15px] xs:pr-0">
      <div className="w-full text-base md:block">
        {isBackOffice ? (
          <a className="v2-mb-[50px] xs:v2-mb-[32px] sm:v2-mb-[32px] md:v2-mb-[50px] block" href={logoPath}>
            <Image className="md:ml-0" src={logo} width={299} alt="pawTree" />
          </a>
        ) : (
          <Link className="v2-mb-[50px] xs:v2-mb-[32px] sm:v2-mb-[32px] md:v2-mb-[50px] block" href={logoPath}>
            <Image className="md:ml-0" src={logo} width={299} alt="pawTree" />
          </Link>
        )}
        <div className="md:text-left">
          <ButtonLink
            as={'a'}
            type="button"
            className="!v2-bg-green-600 !v2-border-green-600 hover:!v2-text-white hover:!v2-bg-transparent v2-text-white !v2-rounded-xs v2-font-sweet-sans-pro v2-font-normal !v2-text-base v2-max-w-[190px] !v2-px-[15px] !v2-py-[9px] md:v2-h-[45px] sm:v2-h-[45px] xs:v2-h-[45px] v2-flex"
            scheme="primary"
            variant="filled"
            href={
              (isBackOffice ? 'https://shop.pawtree.com' : '') +
              generateURL('/enrollment', isBackOffice ? customer?.webAlias || customer?.company : webalias)
            }
          >
            {isBackOffice ? 'Enroll a petPro' : 'Make Money With Us!'}
          </ButtonLink>
        </div>

        {/* <a href="#" onClick={e => e.preventDefault()}>
          <Image className="mt-0 sm:mx-auto md:ml-0" src={inc500} width={90} alt="pawTree" />
        </a> */}
      </div>
    </div>
  );
}

const FootLink = (props: FootLinkProps) => {
  const { children, label, className, as, ...restProps } = props;

  const Comp = as === 'a' ? 'a' : Link;

  return (
    <Comp
      {...restProps}
      className={cn(
        'v2-text-white hover:text-v2-underline flex items-center whitespace-nowrap transition-colors sm:w-full sm:justify-start md:w-fit md:justify-start',
        className
      )}
      {...(as !== 'a' ? { prefetch: false } : {})}
    >
      {/* <ArrowCircleRightOutlined className="mr-1 !text-lg" /> */}
      {label || children}
    </Comp>
  );
};

const LinksColumn = (props: LinksColumnProps) => {
  const { children: child } = props;
  const children = Array.isArray(child) ? child : [child];

  return (
    <div className="sm:mt-11 sm:pl-0 md:mt-0 md:justify-self-center md:pl-[110px] xs:pl-0">
      <h3 className="v2-text-xl v2-text-white v2-font-sweet-sans-pro-medium v2-font-medium md:v2-mb-3 xs:v2-mb-4 sm:v2-mb-4 flex items-end tracking-wide sm:justify-start md:justify-start">
        Quick Links
      </h3>

      <ul className="v2-font-sweet-sans-pro-light v2-font-light v2-text-base w-full text-grayscale-70">
        {children.map((child, idx) => (
          <li className="my-2 w-full last:my-0 sm:text-left md:text-left" key={`footlink-${child?.toString()}-${idx}`}>
            {child}
          </li>
        ))}
      </ul>
    </div>
  );
};

const SocialLink = (props: SocialLinkProps) => {
  const { href, children } = props;

  return (
    <a className="" href={href} target="_blank">
      {children}
    </a>
  );
};

const SocialConnectionsColumn = (props: SocialConnectionsColumnProps) => {
  const { children: child } = props;
  const children = Array.isArray(child) ? child : [child];
  const children1 = children.slice(0, children.length);
  // const children2 = children.slice(4, children.length);

  return (
    <div className="pl-[0px] sm:mt-8 sm:pl-0 md:mt-0 md:justify-self-center md:pl-[0px] xs:pl-0">
      <h3 className="v2-text-xl v2-text-white v2-font-sweet-sans-pro-medium v2-font-medium md:v2-mb-3 xs:v2-mb-4 sm:v2-mb-4 flex items-end tracking-wide sm:justify-start md:justify-start">
        Our Company
      </h3>
      <p className="v2-text-base v2-leading-7 v2-text-white v2-font-sweet-sans-pro-light v2-font-light v2-mb-6 md:v2-mb-6 xs:v2-mb-4 sm:v2-mb-4 sm:text-left md:text-left">
        Our vision is to create a world filled with unconditional love where pets and their people thrive.
      </p>
      <a
        href="mailto:support@pawtree.com"
        className="v2-font-sweet-sans-pro-light v2-mb-3 xs:v2-mb-4 sm:v2-mb-4 md:v2-mb-3 flex items-center gap-3 sm:justify-start md:justify-start"
      >
        {/* <p className="text-primary">
          <Image src={IconMail} width={24} height={24} alt="Mail Icon" />
        </p> */}
        <p className=" v2-text-white">support@pawtree.com</p>
      </a>
      <a
        href={'tel:855-940-5234'}
        className="v2-text-white v2-font-sweet-sans-pro-light v2-mb-[26px] xs:v2-mb-4 sm:v2-mb-4 md:v2-mb-[26px] flex items-center gap-3 sm:justify-start md:justify-start"
      >
        {/* <p className="text-primary">
          <Image src={IconWhatsApp} width={24} height={24} alt="WhatsApp Icon" />
        </p> */}
        <p>855-940-5234</p>
      </a>

      <ul className="v2-items-center v2-gap-[10px] md:v2-gap-[10px] xs:v2-gap-2 sm:v2-gap-2 flex flex-row flex-wrap sm:justify-start md:justify-start">
        {children1.map((child, idx) => (
          <li className="v2-h-[35px] v2-w-[35px]" key={`footlink-social-${child?.toString()}-${idx}`}>
            {child}
          </li>
        ))}
      </ul>
      {/* <ul className="mt-2 flex flex-row flex-wrap gap-2">
        {children2.map((child, idx) => (
          <li key={`footlink-social-${child?.toString()}-${idx}`}>{child}</li>
        ))}
      </ul> */}
    </div>
  );
};

function NewsLetterColumn() {
  const ref = useRef<HTMLButtonElement>(null);
  const [newsletterObj, setNewsletterObj] = useState({ firstName: '', lastName: '', email: '' });
  const [newsLetterSignUp, { isLoading }] = useNewsletterSignUpMutation();
  const { webalias } = useWebAlias();

  const signUpNewsletter = async (data: CustomFormData) => {
    const emailRegex = /^((?!\.)[\w+\-_.]*[^.])(@\w+)(\.\w+(\.\w+)?[^.\W])$/gm;
    const emptyFields = Object.keys(data).filter(k => !data[k]);

    if (emptyFields.length) {
      return toast('Please fill up the details.', { type: 'error' });
    }
    if (!emailRegex.test(data.email as string)) {
      return toast('Please enter a valid email address.', { type: 'error' });
    }

    await newsLetterSignUp({
      firstName: data.firstName as string,
      lastName: data.lastName as string,
      email: data.email as string,
      webalias: webalias,
    })
      .unwrap()
      .then(res => {
        toast(res.message || res.exceptionMessage, { type: res.success ? 'success' : 'error' });
        setNewsletterObj({ firstName: '', lastName: '', email: '' });
      })
      .catch((err: ResponseError) => {
        toast(err?.data?.title, { type: 'error' });
      });
  };

  const handleChange = (e: any) => {
    setNewsletterObj(item => ({ ...item, [e.target.name]: e.target.value }));
  };

  return (
    <div className="v2-mx-auto v2-relative v2-z-[1] -v2-mb-[22px] md:v2-px-0 xs:v2-px-4 sm:v2-px-4 max-w-[730px]">
      <h3 className="v2-text-center v2-font-sweet-sans-pro v2-font-normal v2-text-4xl md:v2-text-4xl xs:v2-text-3xl sm:v2-text-3xl v2-mb-6 md:v2-m-auto xs:v2-m-auto sm:v2-m-auto md:v2-mb-6 v2-max-w-none md:v2-max-w-none xs:v2-max-w-[230px] sm:v2-max-w-[230px] xs:v2-mb-[17px] sm:v2-mb-[17px] v2-text-[#3B3B3B]">
        Get More Than Just Tips!
      </h3>
      <p className="v2-text-[#3B3B3B] v2-text-lg md:v2-text-lg xs:v2-text-base sm:v2-text-base md:v2-leading-[30px] xs:v2-leading-[24px] sm:v2-leading-[24px] v2-font-sweet-sans-pro-light v2-font-light v2-mb-[27px] md:v2-mb-[27px] xs:v2-mb-[28px] sm:v2-mb-[28px] v2-mx-auto v2-text-center v2-max-w-[570px] v2-leading-[30px]">
        Receive proactive pet parenting tips, special offers, surprises and more shared exclusively with our pawTree
        family.
      </p>

      <Form onSubmit={signUpNewsletter}>
        <div className="">
          <div className="v2-gap-2 flex sm:flex-wrap md:flex-nowrap xs:flex-wrap">
            <div className="v2-w-full">
              <TextInput
                type="text"
                name="firstName"
                variant="boxed"
                className="hyros-first-name v2-h-[55px] !v2-rounded-[4px] v2-font-sweet-sans-pro-light v2-font-light v2-text-lg focus:!v2-border-primary focus:!v2-shadow-none !v2-m-0 !v2-mb-[10px] md:!v2-mb-[10px] xs:!v2-mb-0 sm:!v2-mb-0 relative w-full border-[#C8D2D1] px-5 pt-[12px] pb-3"
                placeholder="First Name"
                value={newsletterObj.firstName}
                onChange={handleChange}
              ></TextInput>
            </div>
            <div className="v2-w-full">
              <TextInput
                type="text"
                name="lastName"
                variant="boxed"
                className="hyros-last-name v2-h-[55px] !v2-rounded-[4px] v2-font-sweet-sans-pro-light v2-font-light v2-text-lg focus:!v2-border-primary focus:!v2-shadow-none !v2-m-0 !v2-mb-[10px] md:!v2-mb-[10px] relative w-full border-[#C8D2D1] px-5 pt-[12px] pb-3"
                placeholder="Last Name"
                value={newsletterObj.lastName}
                onChange={handleChange}
              ></TextInput>
            </div>
          </div>
          <div>
            <TextInput
              type="email"
              name="email"
              variant="boxed"
              className="hyros-email v2-h-[55px] !v2-rounded-[4px] v2-font-sweet-sans-pro-light v2-font-light v2-text-lg focus:!v2-border-primary focus:!v2-shadow-none !v2-m-0 relative w-full border-[#C8D2D1] px-5 pt-[12px] pb-3"
              placeholder="Email Address"
              value={newsletterObj.email}
              onChange={handleChange}
            >
              <Button
                type="submit"
                className="!v2-font-sweet-sans-pro !v2-rounded-[4px] v2-font-normal !v2-bg-[#4CA30D] hover:!v2-bg-[#4CA30D] hover:!v2-border-[#4CA30D] v2-border-[#4CA30D] v2-border v2-h-[45px] xs:v2-h-[55px] sm:v2-h-[55px] md:v2-h-[45px] !v2-text-lg sm:v2-w-auto md:v2-max-w-[190px] v2-top-[5px] xs:v2-top-0 sm:v2-top-0 md:v2-top-[5px] xs:v2-right-0 sm:v2-right-0 md:lg:v2-right-[5px] lg:v2-right-[5px] xl:v2-right-[5px] md:v2-w-full absolute flex items-center justify-center hover:text-white"
                scheme="primary"
                variant="filled"
                disabled={isLoading}
                onClick={() => {
                  !isLoading && ref?.current?.click();
                }}
              >
                {/* <SendOutlined className="text-lg" /> */}
                <span className="sm:hidden md:flex">Subscribe</span>
                <Image className="sm:flex md:hidden" src={ArrowRight} width={30} alt="Arrow Right" />
              </Button>
            </TextInput>
          </div>

          <div className="hidden">
            <Button
              ref={ref}
              type="submit"
              className="absolute top-1 flex justify-center border-2 border-transparent py-2 px-4 text-lg hover:bg-primary hover:text-white sm:right-0"
              scheme="primary"
              variant="filled"
            >
              <SendOutlined className="text-lg" />
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
}

const FootBanner = (props: FootBannerProps) => {
  const { children: child } = props;
  const children = Array.isArray(child) ? child : [child];

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      className="v2-bg-primary-600 relative"
      // style={{
      //   backgroundImage: `url(${FooterBackGroundImage.src})`,
      //   backgroundPosition: 'bottom center',
      //   backgroundRepeat: 'no-repeat',
      // }}
    >
      <Container className="xl:px-[15px]">
        <div
          className={`v2-pt-[95px] md:v2-pt-[95px] sm:v2-pt-[70px] xs:v2-pt-[70px] xs:v2-px-[16px] sm:v2-px-[16px] md:v2-px-[0px] v2-pb-[45px] -mx-[15px] grid font-montserrat sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3`}
        >
          {children.map((component, idx) => (
            <div className="mx-auto w-full" key={`${component?.toString()}-${idx}`}>
              {component}
            </div>
          ))}
        </div>
      </Container>
      <div className="v2-h-14 v2-absolute v2-z-10 -v2-top-44 v2-right-12 v2-w-14 v2-flex v2-items-center v2-content-center v2-bg-primary-600 v2-rounded-xs v2-cursor-pointer md:v2-flex sm:v2-hidden xs:v2-hidden">
        {' '}
        <Image
          src={MoveToTopImage}
          alt="Move to top"
          className="mx-auto block h-6 w-6 cursor-pointer"
          onClick={handleScrollToTop}
        />
      </div>
    </div>
  );
};

LinksColumn.Link = FootLink;
SocialConnectionsColumn.Link = SocialLink;

FootBanner.Branding = BrandingColumn;
FootBanner.NavigationLinks = LinksColumn;
FootBanner.SocialLinks = SocialConnectionsColumn;
FootBanner.Form = NewsLetterColumn;

export default FootBanner;
